import { APP_CONSTANTS } from "@/config";
import type { CommandCenterMessage } from "@/services/command-center";

export const LAST_SEEN_KEY_PREFIX = "@clinisharp-command-center-last-seen:";
export const MENTIONS_KEY_PREFIX = "@clinisharp-command-center-mentions:";
/** Same-tab writes do not raise `storage`, so the store raises its own event. */
export const COMMAND_CENTER_BADGES_EVENT = "clinisharp:command-center-badges";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    return;
  }
  window.dispatchEvent(new Event(COMMAND_CENTER_BADGES_EVENT));
}

export function currentTenantId(): string | null {
  return typeof window === "undefined" ? null : read(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY);
}

export function lastSeenMessageId(tenantId: string): string | null {
  return read(`${LAST_SEEN_KEY_PREFIX}${tenantId}`);
}

export function setLastSeenMessageId(tenantId: string, messageId: string): void {
  if (lastSeenMessageId(tenantId) === messageId) return;
  write(`${LAST_SEEN_KEY_PREFIX}${tenantId}`, messageId);
}

export function mentionCount(tenantId: string): number {
  const parsed = Number.parseInt(read(`${MENTIONS_KEY_PREFIX}${tenantId}`) ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

export function bumpMentionCount(tenantId: string): void {
  write(`${MENTIONS_KEY_PREFIX}${tenantId}`, String(mentionCount(tenantId) + 1));
}

export function clearMentionCount(tenantId: string): void {
  if (mentionCount(tenantId) === 0) return;
  write(`${MENTIONS_KEY_PREFIX}${tenantId}`, "0");
}

export function subscribeToCommandCenterBadges(listener: () => void): () => void {
  window.addEventListener(COMMAND_CENTER_BADGES_EVENT, listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener(COMMAND_CENTER_BADGES_EVENT, listener);
    window.removeEventListener("storage", listener);
  };
}

/**
 * How many messages arrived after the one the reader last saw.
 *
 * `messages` is newest-first, as the API answers. There is no unread route: this
 * counts what this browser holds, which is the thread pages it loaded plus what
 * the shell bridge inserted while the app was open. A count nobody can derive is
 * better absent than invented.
 */
export function unreadCount(
  messages: CommandCenterMessage[],
  lastSeenId: string | null,
  ownUserId: string | null
): number {
  let count = 0;
  for (const message of messages) {
    if (message.id === lastSeenId) return count;
    const own = message.sender.kind === "user" && ownUserId !== null && message.sender.id === ownUserId;
    if (!own) count += 1;
  }
  return count;
}
